import { FC, useMemo, Dispatch, SetStateAction } from 'react'
import { Box, Typography } from '@mui/material'
import dayjs from 'dayjs'

/* Utils */
import { SSMResponseProductLicense } from 'models'

/* Components */
import { ProductLicenseDetailsContainer } from './productLicenseDetails.styles'
import LicenseItem from './LicenseItem'
import SingleSelectField from '../../../../../components/form-components/SingleSelectField'
import { startDateIcon, endDateIcon, annualLicenseCostIcon, numberOfLicensesIcon, priceOfUnitsIcon } from './icons'

const year = new Date().getFullYear()
const licenseYearOptions = [
  `${year - 2}/${year - 1}`,
  `${year - 1}/${year}`,
  `${year}/${year + 1}`, // current year
  `${year + 1}/${year + 2}`,
  `${year + 2}/${year + 3}`
]

interface Props {
  license: SSMResponseProductLicense
  selectedLicenseYear: string
  setSelectedLicenseYear: Dispatch<SetStateAction<string>>
}

const ProductLicenseDetails: FC<Props> = ({ license, selectedLicenseYear, setSelectedLicenseYear }) => {
  const hasVariableCostOrCount = useMemo(() => {
    if (!license.perYear || license.perYear.length <= 1) return false

    const firstYear = license.perYear[0]
    return license.perYear.some((yearData) => yearData.cost !== firstYear.cost || yearData.count !== firstYear.count)
  }, [license.perYear])

  const thisSelectedYear = selectedLicenseYear.split('/')
  const found = license.perYear.find((c) => c.year === parseInt(thisSelectedYear[0], 10))

  const displayData = hasVariableCostOrCount ? found : license.perYear[0]

  const licenseItems = [
    {
      icon: startDateIcon,
      label: 'Start date',
      value: <Typography className="value">{dayjs(license.startDate).format('MM/DD/YYYY')}</Typography>
    },
    {
      icon: endDateIcon,
      label: 'End date',
      value: <Typography className="value">{dayjs(license.endDate).format('MM/DD/YYYY')}</Typography>
    },
    {
      icon: annualLicenseCostIcon,
      label: 'Annual license cost',
      value: (
        <Typography className="value">
          {displayData?.cost != null ? (
            <>
              {displayData?.cost} {hasVariableCostOrCount && <span>{selectedLicenseYear}</span>}
            </>
          ) : (
            <span>Not specified {selectedLicenseYear}</span>
          )}
        </Typography>
      ),
      tooltipValue: hasVariableCostOrCount ? (
        <div className="tooltip-wrapper">
          {license.perYear.map((item) => (
            <div className="tooltip-item" key={item.year}>
              <Typography className="tooltip-item-year">
                {item.year}
                {item.year === year && <span className="current-year-text">Current year</span>}
              </Typography>
              <Typography className="tooltip-item-cost">${item.cost}</Typography>
            </div>
          ))}
        </div>
      ) : undefined
    },
    {
      icon: numberOfLicensesIcon,
      label: 'Number of licenses',
      value: (
        <Typography className="value">
          {displayData?.count != null ? (
            <>
              {displayData?.count} {hasVariableCostOrCount && <span>{selectedLicenseYear}</span>}
            </>
          ) : (
            <span>Not specified {selectedLicenseYear}</span>
          )}
        </Typography>
      ),
      tooltipValue: hasVariableCostOrCount ? (
        <div className="tooltip-wrapper">
          {license.perYear.map((item) => (
            <div className="tooltip-item" key={item.year}>
              <Typography className="tooltip-item-year">
                {item.year}
                {item.year === year && <span className="current-year-text">Current year</span>}
              </Typography>
              <Typography className="tooltip-item-cost">{item.count}</Typography>
            </div>
          ))}
        </div>
      ) : undefined
    },
    {
      icon: priceOfUnitsIcon,
      label: 'Pricing units',
      value: <Typography className="value">{license.entityType || ''}</Typography>
    }
  ]

  return (
    <ProductLicenseDetailsContainer className="product-license-details-container">
      <Box className="product-license-header">
        {hasVariableCostOrCount && (
          <Box className="product-license-header">
            <SingleSelectField
              options={licenseYearOptions}
              placeholder=""
              label=""
              value={selectedLicenseYear}
              handleChange={(e: any) => {
                setSelectedLicenseYear(e.target.value)
              }}
              disabled={false}
              minWidth="144px !important"
              fullWidth
              licenseYearField
            />
          </Box>
        )}
      </Box>

      <Typography className="license-details-heading">License Details</Typography>
      {licenseItems.map((item, index) => (
        <LicenseItem key={index} {...item} />
      ))}
    </ProductLicenseDetailsContainer>
  )
}

export default ProductLicenseDetails
